import React from 'react'
import { Link as RouterLink } from 'react-router-dom'

import { Box, Link, Skeleton, Typography } from '@mui/material'

import { GRTooltip } from '../../../../components/GRTooltip/GRTooltip'
import { colors } from '../../../../themes/theme'
import { EventLevelTooltipContent, HierarchyLevel } from '../EventLevelTooltipContent/EventLevelTooltipContent'

// The band sits a shade off the surrounding white so the four levels read as
// one strip.
const HIERARCHY_BAND_BORDER = `1px solid ${colors.ruleSubtle}`

export type { HierarchyLevel }

// Relative column widths per level, in hierarchy order. A level the event
// cannot name drops out and the remaining columns share the row between them,
// so the band never shows an empty slot.
const HIERARCHY_COLUMN_WEIGHTS: Record<HierarchyLevel, string> = {
  category: '0.9fr',
  type: '1.3fr',
  blueprintFamily: '1.2fr',
  blueprint: '1fr',
}

export interface HierarchyCell {
  key: HierarchyLevel
  label: string
  value: string
  // Where the value leads: a route, an in-place handler, or neither (plain text).
  to?: string
  onClick?: () => void
  // Analytics pages leave the current surface in a new window so it stays put.
  openInNewWindow?: boolean
  // The category colour, drawn as a dot in front of the category name. Only the
  // category cell carries it, matching the live event dialog hierarchy path.
  accentColor?: string
  // The level exists but has no value yet (a blueprint no analyst has mapped to
  // a family). Rendered muted, with `note` explaining the gap on hover.
  isPlaceholder?: boolean
  note?: string
  // Drawn after the value, e.g. a discontinued marker.
  adornment?: React.ReactNode
}

const hierarchyValueSx = { fontSize: '0.8125rem', lineHeight: 1.35, minWidth: 0, overflowWrap: 'anywhere' } as const

// The level caption, explaining itself on hover. The label is what names the
// level, so it carries the explanation; the value below it is the event's own
// data and leads somewhere instead.
const HierarchyLabel: React.FC<{ cell: HierarchyCell }> = ({ cell }) => {
  return (
    <GRTooltip
      content={<EventLevelTooltipContent level={cell.key} showLevelDetails />}
      styles={{ cursor: 'help', display: 'inline-block', marginBottom: '1px', alignSelf: 'flex-start' }}
    >
      <Typography variant="caption" component="div" sx={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: colors.textMuted }}>
        {cell.label}
      </Typography>
    </GRTooltip>
  )
}

const HierarchyValue: React.FC<{ cell: HierarchyCell }> = ({ cell }) => {
  if (cell.isPlaceholder) {
    const text = (
      <Typography variant="body2" sx={{ ...hierarchyValueSx, color: 'text.disabled' }}>
        {cell.value}
      </Typography>
    )
    return cell.note ? (
      <GRTooltip content={cell.note} styles={{ cursor: 'help', display: 'inline-flex', minWidth: 0 }}>
        {text}
      </GRTooltip>
    ) : (
      text
    )
  }
  if (cell.to) {
    return (
      <Link
        component={RouterLink}
        to={cell.to}
        variant="body2"
        underline="none"
        sx={hierarchyValueSx}
        {...(cell.openInNewWindow ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {cell.value}
      </Link>
    )
  }
  if (cell.onClick) {
    return (
      <Link component="button" type="button" onClick={cell.onClick} variant="body2" underline="none" sx={{ ...hierarchyValueSx, textAlign: 'left' }}>
        {cell.value}
      </Link>
    )
  }
  return (
    <Typography variant="body2" sx={hierarchyValueSx}>
      {cell.value}
    </Typography>
  )
}

interface LiveEventHierarchyBandProps {
  // The levels the event can name, in hierarchy order (Category › Type ›
  // Blueprint family › Blueprint). Nothing renders for an empty list unless
  // the band is loading.
  cells: HierarchyCell[]
  // Keeps the strip in place while the event behind it is still in flight.
  isLoading?: boolean
}

/**
 * Labelled strip naming where a tracked live event sits in the taxonomy:
 * one cell per level, a small uppercase caption over the value, the category
 * colour as a dot before the category name. Full width of its container with a
 * hairline above and below, so it doubles as the seam between a header and
 * the content under it. Shared by the LET feed card and the event dialog so
 * the two read the same.
 */
export const LiveEventHierarchyBand: React.FC<LiveEventHierarchyBandProps> = ({ cells, isLoading = false }) => {
  if (!isLoading && cells.length === 0) return null

  return (
    <Box
      sx={{
        bgcolor: colors.surfaceTintSubtle,
        borderTop: HIERARCHY_BAND_BORDER,
        borderBottom: HIERARCHY_BAND_BORDER,
        // The next sibling is a spacing-4 Grid whose negative top margin reaches
        // into the bottom of the band. Without its own paint layer the band's
        // links lose hit-testing there, so only their top half is clickable.
        position: 'relative',
        zIndex: 0,
      }}
    >
      {isLoading ? (
        <Box sx={{ px: 2, py: 0.875, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Skeleton variant="text" width={72} sx={{ fontSize: 9 }} />
          <Skeleton variant="text" width={280} sx={{ fontSize: '0.8125rem' }} />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            // Weighted columns for whichever levels resolved; two-up and then
            // single-column below md.
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: cells.map((cell) => HIERARCHY_COLUMN_WEIGHTS[cell.key]).join(' '),
            },
          }}
        >
          {cells.map((cell, index) => (
            <Box
              key={cell.key}
              sx={{
                px: 2,
                py: 0.875,
                minWidth: 0,
                // Cells stretch to the tallest one (a wrapped value), so the
                // shorter ones center their pair rather than hanging from the top.
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                // Vertical seams only once the cells share a single row.
                borderRight: { xs: 'none', md: index < cells.length - 1 ? HIERARCHY_BAND_BORDER : 'none' },
              }}
            >
              <HierarchyLabel cell={cell} />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                {cell.accentColor && <Box aria-hidden sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cell.accentColor, flexShrink: 0 }} />}
                <HierarchyValue cell={cell} />
                {cell.adornment}
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
